import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  configureApiFetchAuth,
  fetchAuthSession,
} from "../services/apiFetch";
import { AuthBootstrapContext } from "./useAuthBootstrap";
import { resolveAnonymousEntry } from "../features/onboarding/anonymousEntry";
import { loadAnonymousOnboardingDraft } from "../features/onboarding/onboardingDraftStore";
import { releaseLocalRecoverySuppression } from "../features/workoutLog/recovery/recoverySuppression";
import { workoutLogStore } from "../features/workoutLog/store/localExecutionStore";
import { workoutSyncEngine } from "../features/workoutLog/sync/syncEngine";

const PRESENTATION_THRESHOLDS = Object.freeze({
  brand: 400,
  shortCopy: 2500,
  longCopy: 10000,
});
const DEFAULT_BOOTSTRAP_TIMEOUT_MS = 60000;

function readBootstrapTimeout(value = process.env.REACT_APP_BOOTSTRAP_TIMEOUT_MS) {
  if (value == null || String(value).trim() === "") {
    return DEFAULT_BOOTSTRAP_TIMEOUT_MS;
  }
  const timeout = Number(value);
  return Number.isSafeInteger(timeout) && timeout > 0
    ? timeout
    : DEFAULT_BOOTSTRAP_TIMEOUT_MS;
}

const INITIAL_STATE = Object.freeze({
  status: "resolving",
  user: null,
  onboarding: null,
  csrfToken: null,
  providers: [],
  pendingGeneration: null,
  draft: null,
  anonymousEntry: null,
});

function toResolvedState(result) {
  if (result.status === 401) {
    const draft = loadAnonymousOnboardingDraft();
    return {
      ...INITIAL_STATE,
      status: "anonymous",
      providers: Array.isArray(result.body?.providers) ? result.body.providers : [],
      draft,
      anonymousEntry: resolveAnonymousEntry({ draft }),
    };
  }

  return {
    status: "authenticated",
    user: result.body?.user || null,
    onboarding: result.body?.onboarding || null,
    csrfToken: result.body?.csrfToken || null,
    providers: Array.isArray(result.body?.providers) ? result.body.providers : [],
    pendingGeneration: result.body?.pendingGeneration || null,
    draft: null,
    anonymousEntry: null,
  };
}

// The outcome of a background Session re-resolution. A network failure, a
// timeout or a run superseded by a newer one decides nothing: "unavailable".
function toResolution(result) {
  return result?.status === "authenticated" || result?.status === "anonymous"
    ? result
    : { status: "unavailable" };
}

async function maintainWorkoutLogStorage(localWorkoutStore, userId) {
  const operations = [
    ["record authenticated owner", () => localWorkoutStore.setLastAuthenticatedUserId(userId)],
    // Signed in again: an earlier explicit logout of this same user no longer
    // suppresses their local recovery (never another user's suppression).
    ["release logout recovery suppression", () => releaseLocalRecoverySuppression(userId)],
    ["purge expired acknowledged records", () => localWorkoutStore.purgeExpiredAcknowledged(userId)],
  ];
  for (const [operation, run] of operations) {
    try {
      await run();
    } catch (error) {
      console.warn(`Workout Log could not ${operation}.`, error);
    }
  }
}

export default function AuthBootstrapProvider({
  children,
  localWorkoutStore = workoutLogStore,
  workoutEngine = workoutSyncEngine,
}) {
  const [state, setState] = useState(INITIAL_STATE);
  const [presentation, setPresentation] = useState("hidden");
  const requestRef = useRef({ id: 0, controller: null, timers: [] });
  const csrfTokenRef = useRef(null);
  const refreshRef = useRef(null);
  const refreshCsrfRef = useRef(null);
  const stateRef = useRef(INITIAL_STATE);
  // The bootstrap run in flight, whatever its kind, so a re-resolution joins it
  // instead of cancelling it (AuthReturnScreen and Retry keep their result).
  const currentRunRef = useRef(null);
  const resolutionRef = useRef(null);
  // The Workout auth-loss episode already being handled, one at a time:
  // `{ userId, failed }`, or null when the next AUTH_REQUIRED starts a new one.
  const authEpisodeRef = useRef(null);

  const cancelCurrentRequest = useCallback(() => {
    requestRef.current.timers.forEach(clearTimeout);
    requestRef.current.timers = [];
    requestRef.current.controller?.abort();
    requestRef.current.controller = null;
    requestRef.current.id += 1;
  }, []);

  // A visible run's non-authenticated states reach stateRef and the sync engine
  // too: TRAIN can now stay mounted in local mode through them (§9.8), and the
  // engine must never push for a user the UI no longer treats as authenticated.
  const followUnresolved = useCallback((status) => {
    stateRef.current = { ...stateRef.current, status };
    workoutEngine.setBootstrap(stateRef.current);
  }, [workoutEngine]);

  const executeBootstrap = useCallback(async ({ silent = false } = {}) => {
    cancelCurrentRequest();
    const requestId = requestRef.current.id + 1;
    const controller = new AbortController();
    requestRef.current = { id: requestId, controller, timers: [] };
    if (!silent) {
      setState((current) => ({ ...current, status: "resolving" }));
      followUnresolved("resolving");
      setPresentation("hidden");
    }

    const schedule = (callback, delay) => {
      const timer = setTimeout(() => {
        if (requestRef.current.id === requestId) {
          callback();
        }
      }, delay);
      requestRef.current.timers.push(timer);
    };
    if (!silent) {
      schedule(() => setPresentation("brand"), PRESENTATION_THRESHOLDS.brand);
      schedule(() => setPresentation("short"), PRESENTATION_THRESHOLDS.shortCopy);
      schedule(() => setPresentation("long"), PRESENTATION_THRESHOLDS.longCopy);
    }
    schedule(() => controller.abort(), readBootstrapTimeout());

    try {
      const result = await fetchAuthSession({ signal: controller.signal });
      if (requestRef.current.id !== requestId) {
        return null;
      }
      requestRef.current.timers.forEach(clearTimeout);
      requestRef.current.timers = [];
      requestRef.current.controller = null;
      if (!silent) {
        setPresentation("hidden");
      }
      const nextState = toResolvedState(result);
      csrfTokenRef.current = nextState.csrfToken;
      stateRef.current = nextState;
      setState(nextState);
      // An AUTH_REQUIRED episode ends once the Session is no longer its user's.
      if (
        authEpisodeRef.current &&
        !(nextState.status === "authenticated" &&
          nextState.user?.id === authEpisodeRef.current.userId)
      ) {
        authEpisodeRef.current = null;
      }
      workoutEngine.setBootstrap(nextState);
      if (nextState.status === "authenticated" && nextState.user?.id &&
        typeof indexedDB !== "undefined") {
        void maintainWorkoutLogStorage(localWorkoutStore, nextState.user.id);
      }
      return nextState;
    } catch (error) {
      if (requestRef.current.id !== requestId) {
        return null;
      }
      requestRef.current.timers.forEach(clearTimeout);
      requestRef.current.timers = [];
      requestRef.current.controller = null;
      if (silent) {
        throw error;
      }
      setPresentation("hidden");
      let nextState = null;
      setState((current) => {
        nextState = { ...current, status: "unavailable" };
        return nextState;
      });
      followUnresolved("unavailable");
      return nextState;
    }
  }, [cancelCurrentRequest, followUnresolved, localWorkoutStore, workoutEngine]);

  const runBootstrap = useCallback((options) => {
    const run = executeBootstrap(options);
    currentRunRef.current = run;
    const settle = () => {
      if (currentRunRef.current === run) currentRunRef.current = null;
    };
    run.then(settle, settle);
    return run;
  }, [executeBootstrap]);

  const retry = useCallback(() => runBootstrap(), [runBootstrap]);
  const refresh = useCallback(
    () => runBootstrap({ silent: true }),
    [runBootstrap]
  );

  /**
   * Deduplicated background Session re-resolution (Phase 8, §11.3, §11.4, §9.8)
   * on the existing bootstrap run. Concurrent callers share one request, and a
   * run already in flight is joined rather than cancelled. It resolves to the
   * committed state (`authenticated` / `anonymous`) or `{ status:
   * "unavailable" }`, and never rejects. A failure changes nothing: an
   * authenticated app is never signed out by a background refresh failure.
   */
  const reresolveSession = useCallback(() => {
    if (resolutionRef.current) return resolutionRef.current;
    const pending = currentRunRef.current || runBootstrap({ silent: true });
    const resolution = pending.then(toResolution, () => ({ status: "unavailable" }));
    resolutionRef.current = resolution;
    const settle = () => {
      if (resolutionRef.current === resolution) resolutionRef.current = null;
    };
    resolution.then(settle);
    return resolution;
  }, [runBootstrap]);

  const refreshCsrf = useCallback(async () => {
    const current = stateRef.current;
    const result = await fetchAuthSession();
    if (result.status === 401) {
      return { status: "anonymous" };
    }
    const user = result.body?.user || null;
    if (
      current.status !== "authenticated" ||
      !user?.id ||
      user.id !== current.user?.id
    ) {
      // The browser Session belongs to someone else (H8-3). The global state
      // converges to it through the one bootstrap authority; TRAIN's identity
      // and remount rules then apply. The caller never retries as that user.
      void reresolveSession();
      return { status: "different-user" };
    }
    const csrfToken = result.body?.csrfToken || null;
    csrfTokenRef.current = csrfToken;
    setState((latest) => {
      if (latest.status !== "authenticated" || latest.user?.id !== user.id) {
        return latest;
      }
      const next = { ...latest, csrfToken };
      stateRef.current = next;
      return next;
    });
    return { status: "authenticated", csrfToken };
  }, [reresolveSession]);

  // The bootstrap onboarding state is a snapshot taken when the session was
  // resolved. Any authoritative lifecycle response the app receives afterwards
  // must be folded back in, otherwise route guards keep enforcing a status the
  // server has already moved past. Only `onboarding` changes, so callers that
  // depend on `pendingGeneration` identity are not disturbed.
  const applyOnboarding = useCallback((onboarding) => {
    if (!onboarding) {
      return;
    }
    setState((current) => (
      current.status === "authenticated" && current.onboarding !== onboarding
        ? { ...current, onboarding }
        : current
    ));
  }, []);

  const completeLogout = useCallback(() => {
    cancelCurrentRequest();
    csrfTokenRef.current = null;
    const draft = loadAnonymousOnboardingDraft();
    const next = {
      ...INITIAL_STATE,
      status: "anonymous",
      providers: stateRef.current.providers,
      draft,
      anonymousEntry: resolveAnonymousEntry({ draft }),
      // An explicit, server-confirmed logout, never a Session loss: TRAIN does
      // not keep or re-derive local recovery for the signed-out user (§11.6).
      loggedOut: true,
    };
    authEpisodeRef.current = null;
    stateRef.current = next;
    setState(next);
    workoutEngine.setBootstrap(next);
    setPresentation("hidden");
  }, [cancelCurrentRequest, workoutEngine]);

  useEffect(() => {
    workoutEngine.start();
    retry();
    return () => {
      cancelCurrentRequest();
      workoutEngine.stop();
    };
  }, [cancelCurrentRequest, retry, workoutEngine]);

  // A Workout 401 does not touch the global bootstrap (H8-1). One auth-loss
  // episode = one re-resolution, in the background (§11.3):
  //   starts  - the first AUTH_REQUIRED for the current authenticated user;
  //   ignores - every later AUTH_REQUIRED while it lasts (status events, edits,
  //             scheduler triggers and renders cannot add requests);
  //   ends    - the server accepts a push for that user (SYNC_ACCEPTED), or the
  //             committed Session is no longer theirs (anonymous, another user,
  //             logout). Only then can a new 401 start a new episode.
  // Re-resolving as the same user does not end it: the resumed push proves it.
  // Were /session and the Workout API to disagree, that is what stops a
  // 401 -> refresh -> push -> 401 loop. A failed attempt ("unavailable") keeps
  // the episode but lets the next `online` / visible signal try once more.
  const resolveEpisode = useCallback((episode) => {
    episode.failed = false;
    reresolveSession().then((resolution) => {
      if (resolution.status === "unavailable" && authEpisodeRef.current === episode) {
        episode.failed = true;
      }
    });
  }, [reresolveSession]);

  useEffect(() => {
    if (typeof workoutEngine.subscribe !== "function") return undefined;
    const unsubscribe = workoutEngine.subscribe((event) => {
      const userId = event?.userId;
      if (event?.type === "SYNC_ACCEPTED") {
        if (authEpisodeRef.current?.userId === userId) authEpisodeRef.current = null;
        return;
      }
      if (event?.type !== "AUTH_REQUIRED" || !userId) return;
      const current = stateRef.current;
      if (current.status !== "authenticated" || current.user?.id !== userId) return;
      if (authEpisodeRef.current?.userId === userId) return;
      authEpisodeRef.current = { userId, failed: false };
      resolveEpisode(authEpisodeRef.current);
    });
    return () => {
      unsubscribe?.();
    };
  }, [resolveEpisode, workoutEngine]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const retryFailedEpisode = () => {
      const episode = authEpisodeRef.current;
      const current = stateRef.current;
      if (
        !episode?.failed ||
        window.navigator?.onLine === false ||
        document.visibilityState === "hidden" ||
        current.status !== "authenticated" ||
        current.user?.id !== episode.userId
      ) {
        return;
      }
      resolveEpisode(episode);
    };
    window.addEventListener("online", retryFailedEpisode);
    document.addEventListener("visibilitychange", retryFailedEpisode);
    return () => {
      window.removeEventListener("online", retryFailedEpisode);
      document.removeEventListener("visibilitychange", retryFailedEpisode);
    };
  }, [resolveEpisode]);

  useEffect(() => {
    refreshRef.current = refresh;
    refreshCsrfRef.current = refreshCsrf;
    return configureApiFetchAuth({
      getCsrfToken: () => csrfTokenRef.current,
      refresh: async () => refreshRef.current?.(),
      refreshCsrf: async () => refreshCsrfRef.current?.(),
    });
  }, [refresh, refreshCsrf]);

  const value = useMemo(
    () => ({
      ...state,
      presentation,
      retry,
      refresh,
      refreshCsrf,
      reresolveSession,
      applyOnboarding,
      completeLogout,
    }),
    [
      applyOnboarding,
      completeLogout,
      presentation,
      refresh,
      refreshCsrf,
      reresolveSession,
      retry,
      state,
    ]
  );

  return (
    <AuthBootstrapContext.Provider value={value}>
      {children}
    </AuthBootstrapContext.Provider>
  );
}

export {
  DEFAULT_BOOTSTRAP_TIMEOUT_MS,
  INITIAL_STATE,
  PRESENTATION_THRESHOLDS,
  maintainWorkoutLogStorage,
  readBootstrapTimeout,
};
